import React, { useEffect, useState, useMemo, useRef, useContext } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FilterCoverage from "./FilterCoverage";
import SearchCoverage from "./SearchCoverage";
import Statement from "../CommonComponent/Statement";
import DisplayCoverage from "./DisplayCoverage";
import Footer from "../CommonComponent/Footer";
import {
  AuthenticationService,
  ConfigurationService,
  CoverageService,
  AppSettingsService,
  CommonService,
  StyleService,
  PendoService,
} from "@services";
import DynamicHtml from "../CommonComponent/DynamicHtml";
import BenefitTypeButton from "../CommonComponent/BenefitTypeButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import StateMap from "../CommonComponent/StateMap";
import DebugMessage from "../CommonComponent/DebugMessage";
import { ConfigurationContext } from "../../../contexts/Configuration";
import { SearchContext } from "../../../contexts/Search";
import LoaderWithIcon from "../CommonComponent/LoaderWithIcon";

const HCPCoverageFinder = ({ token, indicationid, zipCode }) => {
  const NO_RESULTS_FOUND_MESSAGE = "No results found";
  const defaultChannels = [
    "Show All",
    "Commercial",
    "Health Exchange",
    "Medicare",
    "Managed Medicaid",
    "State Medicaid",
  ];
  const matchesMobileWidth = useMediaQuery("(max-width:1032px)");
  const { appliedConfiguration, setAppliedConfiguration, settings, setSettings } = useContext(ConfigurationContext);
  const { searchTermDisplay, setSearchTermDisplay, benefitType, setBenefitType, setProductId, setProductName, setIndicationName } = useContext(SearchContext);
  const [showResults, setShowResults] = useState(false);
  const [selectedBenefitType, setSelectedBenefitType] = useState("");
  const [benefitTypes, setBenefitTypes] = useState([]);
  const [showBenefitType, setShowBenefitType] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [disableBenefitType, setDisableBenefitType] = useState(true);
  const [searchNPI, setNPI] = useState("");
  const [selectedIndication, setSelectedIndication] = useState(null);
  const [gridColumnsBothBenefits, setGridColumnsBothBenefits] = useState([]);
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [channels, setChannels] = useState(defaultChannels);
  const [clientProductConfigurations, setClientProductConfigurations] = useState(null);
  const [customFooter, setCustomFooter] = useState("");
  const [resultMessage, setResultMessage] = useState("");
  const [isLoadingCoverage, setIsLoadingCoverage] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [stylePath, setStylePath] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [planNameFilter, setPlanNameFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [dynamicStatement, setDynamicStatement] = useState("");
  const [header, setHeader] = useState("");
  const [subHeader, setSubHeader] = useState("");
  const [websiteType, setWebsiteType] = useState("1");
  const [selectedState, setSelectedState] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [stateId, setStateId] = useState("");
  const coverageTableContainerRef = useRef();
  const [visibleRows, setvisibleRows] = useState([]);
  const [debugMode, setDebugMode] = useState(false);
  const [noDataWarning, setNoDataWarning] = useState("");
  const [filterValue, setFilterValues] = useState([]);
  const [showDataSource, setShowDataSource] = useState(false);
  const [isFilterSame, setIsFilterSame] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const dynamicHtmlRef = useRef(null);
  const [showAllTabName, setShowAllTabName] = useState("Show All");
  const [coverageFinderGroupIdValue, setcoverageFinderGroupId] = useState(0);
  const [groupProducts, setgroupProducts] = useState([]);
  const savedScrollPositionRef = useRef({ top: 0, left: 0 });
  let tokenProducts = [];
  let coverageFinderGroupId = 0;
  useEffect(() => {
    let ignore = false;
    if (token) {
      const jwtPayload = CommonService.parseJwt(token);
      tokenProducts = JSON.parse(jwtPayload.products);
      setProductName(tokenProducts[0].Name);
      setProductId(tokenProducts[0].ProductId);
      if (jwtPayload?.coverageFinderGroupId !== undefined) {
        coverageFinderGroupId = jwtPayload.coverageFinderGroupId;
        setcoverageFinderGroupId(coverageFinderGroupId);
      }
      setProducts(tokenProducts);
      const jwtWebsiteType = jwtPayload.websiteType ?? 1;
      setWebsiteType(jwtWebsiteType);
      const cdnUrl = AppSettingsService.getInstance().getCDNUrl();
      if (tokenProducts.length === 1) {
        setStylePath(`${cdnUrl}css/${jwtPayload.clientId}_${tokenProducts[0].ProductId}_${jwtWebsiteType}.css`);
      }
    } else {
      console.log("Token is null or empty");
    }

    async function getConfig() {
      const requestBody = (coverageFinderGroupId > 0)
        ? { CoverageFinderGroupId: coverageFinderGroupId }
        : { ProductId: tokenProducts?.[0]?.ProductId };

      const configResponse = await ConfigurationService.getInstance().getConfiguration(requestBody);
      if (!ignore) {
        applyConfiguration(configResponse);
        setIsLoadingPage(false);
      }
    }

    if (token) {
      AuthenticationService.getInstance().setToken(token);
      getConfig();
    }

    return () => {
      ignore = true;
    };
  }, [token]);

  // Helper functions for scroll position preservation
  const saveScrollPosition = () => {
    savedScrollPositionRef.current = {
      top: window.pageYOffset || document.documentElement.scrollTop,
      left: window.pageXOffset || document.documentElement.scrollLeft
    };
  };

  const restoreScrollPosition = () => {
    const { top, left } = savedScrollPositionRef.current;
    window.scrollTo(left, top);
  };

  useMemo(() => {
    const newIndication = selectedIndication ?? indicationid;
    UpdateConfigurationOnIndicationChange(newIndication);
  }, [indicationid, selectedIndication, clientProductConfigurations]);

  useMemo(() => {
    if ((planNameFilter != null && planNameFilter != "") || (channelFilter != null && channelFilter != "")) {
      filterCoverageTable(rows, benefitType);
    }
  }, [planNameFilter, channelFilter]);

  useMemo(() => {
    if (matchesMobileWidth)
      sortRows(sortOrder);
    else
      updateVisibleRow();
  }, [filteredRows, order, orderBy, sortOrder]);

  useEffect(() => {
    if (isFilterSame && filterValue.length > 0) {
      handleChangeBenefitType(benefitType);
    }
  }, [isFilterSame, filterValue]);

  useEffect(() => {
    if (selectedBenefitType) {
      let footerText = selectedBenefitType.toLowerCase().includes("pharmacy") ? appliedConfiguration.coverageFinderConfiguration?.pharmacyFooterText : appliedConfiguration.coverageFinderConfiguration?.footerText;
      setCustomFooter(footerText);
      let subHeader = selectedBenefitType.toLowerCase().includes("pharmacy") ? appliedConfiguration.coverageFinderConfiguration?.pharmacySubHeader : appliedConfiguration.coverageFinderConfiguration?.subHeader;
      setSubHeader(subHeader);
    }
  }, [selectedBenefitType]);

  function updateVisibleRow() {
    if (order === "") {
      setvisibleRows(filteredRows);
    } else {
      let sortBy = (orderBy.match("PAFormIcon") || orderBy.match("PrescriberNotes")) ? "PrescriberNotes_DisplayValue" : orderBy;
      let data = stableSortRow(filteredRows, sortBy, order);
      setvisibleRows(data);
    }
  }

  function stableSortRow(array, sortBy, order) {
    const enCollator = new Intl.Collator("en");
    const isCoverageSort = sortBy.includes("_CoverageContingentData");
    let valueA, valueB;

    return array.slice().sort((a, b) => {
      if (isCoverageSort) {
        const keyA = Object.keys(a).find(key => key === sortBy) || "";
        const keyB = Object.keys(b).find(key => key === sortBy) || "";

        const dataA = keyA ? JSON.parse(a[keyA] || "{}") : {};
        const dataB = keyB ? JSON.parse(b[keyB] || "{}") : {};

        const displayPrefixA = CommonService.extractTextFromHtmlText(dataA.displayPrefix)?.trim() || "";
        const displayPrefixB = CommonService.extractTextFromHtmlText(dataB.displayPrefix)?.trim() || "";

        const getRowValue = (data) => {
          if (CommonService.isUrl(data.rowValue))
            return data.iconPath || "";

          return CommonService.extractTextFromHtmlText(data.rowValue)?.trim() || "";
        };

        valueA = displayPrefixA + getRowValue(dataA);
        valueB = displayPrefixB + getRowValue(dataB);

      } else {
        valueA = CommonService.extractTextFromHtmlText(a[sortBy] || "");
        valueB = CommonService.extractTextFromHtmlText(b[sortBy] || "");
      }

      return order === "desc" ? -enCollator.compare(valueA, valueB) : enCollator.compare(valueA, valueB);
    });
  }
  function updateTokenProducts(config) {
    if (coverageFinderGroupIdValue > 0 && config) {
      tokenProducts = groupProducts
        .filter(item => item.indication?.id === selectedIndication)
        .map(item => ({
          ProductId: item.productId,
          Name: item.name
        }));
      setProducts(tokenProducts);
    }
  }
  function UpdateConfigurationOnIndicationChange(newIndicationId) {
    if (clientProductConfigurations?.configurations) {
      if (newIndicationId && appliedConfiguration?.indicationId != newIndicationId) {
        let jwtWebsiteType = Number(CommonService.parseJwt(token).websiteType ?? 1);
        const config = clientProductConfigurations.configurations.find(
          (x) =>
            Number(x.indicationId) === Number(newIndicationId) &&
            x.coverageFinderConfiguration != null &&
            Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType
        );
        if (config) {
          setAppliedConfiguration(config);
          updateTokenProducts(config);
          if (products.length < 2 && config) {
            updateAllConfigValues(config);
          }
          setTimeout(() => {
            restoreScrollPosition();
          }, 1);
        } else {
          console.log("no configuration found for indication");
        }
      } else {
        console.log("indicationId is null or empty");
      }
    }
  }
  function updateConfigurationOnProductChange(newProductId) {
    if (clientProductConfigurations?.configurations) {
      if (newProductId) {
        const selectedGroupProduct = groupProducts.find((item) => item.productId === newProductId);
        const indicationId = selectedIndication || selectedGroupProduct.indication.id;
        let jwtWebsiteType = Number(CommonService.parseJwt(token).websiteType ?? 1);
        const config = clientProductConfigurations.configurations.find(
          (x) =>
            Number(x.indicationId) === Number(indicationId) &&
            x.coverageFinderConfiguration != null &&
            Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType &&
            Number(x.productId) === Number(newProductId)
        );
        if (config) {
          setAppliedConfiguration(config);
          if (config) {
            updateAllConfigValues(config);
            const cdnUrl = AppSettingsService.getInstance().getCDNUrl();
            setStylePath(`${cdnUrl}css/${config?.clientId}_${newProductId}_${jwtWebsiteType}.css`);
            StyleService.getInstance().loadCustomFonts(config?.clientId, cdnUrl, newProductId);
            setTimeout(() => {
              restoreScrollPosition();
            }, 1);
          }
        } else {
          console.log("no configuration found for indication");
        }
      } else {
        console.log("indicationId is null or empty");
      }
    }
  }
  function filterData(data, query, columnsList) {
    if (columnsList.length === 0) return data; // Return data if no columns are specified
    const firstColumn = columnsList[0]; // Get the first column only
    return data.filter((item) => {
      let columnValue = item[firstColumn.dataField];
      return typeof columnValue === "string" && columnValue.toLowerCase().includes(query.toLowerCase());
    });
  }

  function getResultName(name) {
    const lowercaseName = name ? name.toLowerCase() : "";
    switch (lowercaseName) {
      case "controller/payer level":
        return "payers";
      case "medical group level":
        return "medical groups";
      case "formulary level":
        return "formulary groups";
      default:
        return "plans";
    }
  }

  function filterDynamicColumns(benefitTypeId, filterGridColumns, isShowAll) {
    //FTW - SettingId 78: CF - Hide Plan Type Column When a Channel is Selected
    if (!isShowAll && settings?.findIndex((setting) => setting.settingId === 78 && setting.isEnabled === true) > -1) {
      filterGridColumns = filterGridColumns.filter((cell) => cell.dataField !== "Channel");
    }

    if (filterValue.length > 0) {
      filterGridColumns = getFilterGridColumns(benefitTypeId, filterGridColumns);
    }
    setDynamicColumns(filterGridColumns);

    return filterGridColumns;
  }

  function filterCoverageTable(rowsData, newBenefitType) {
    const benefitTypeId = newBenefitType.toLowerCase().includes("pharmacy") ? 2 : 1;
    let filterGridColumns = gridColumnsBothBenefits.filter((p) => p.benefitTypeId === benefitTypeId);
    let filteredData = rowsData;
    let filterDynamicColumn = filterGridColumns;
    //FTW - SettingId 78: CF - Hide Plan Type Column When a Channel is Selected
    if (channelFilter === showAllTabName) {
      filterDynamicColumn = filterDynamicColumns(benefitTypeId, filterGridColumns, true);
      filteredData = rowsData;
    } else {
      filterDynamicColumn = filterDynamicColumns(benefitTypeId, filterGridColumns, false);
      filteredData = rowsData.filter((p) => p.Channel === channelFilter);
    }
    if (planNameFilter) {
      filteredData = filterData(filteredData, planNameFilter, filterDynamicColumn);
    }
    setFilteredRows(filteredData);
    if (appliedConfiguration?.coverageFinderConfiguration) {
      const resultName = getResultName(
        benefitTypeId == 1
          ? appliedConfiguration.coverageFinderConfiguration.resultName
          : appliedConfiguration.coverageFinderConfiguration.pharmacyResultName
      );
      const noDataMessage =
        filteredData.length < 1 && rowsData.length > 0
          ? `There are no ${resultName} with this plan type in this area.`
          : "";

      setNoDataWarning(noDataMessage);
    }
    if (coverageTableContainerRef?.current) {
      coverageTableContainerRef.current.scrollTo(0, 0);
    }
  }
  function updateBenefitType(config) {
    if (config.coverageFinderConfiguration.benefitType.toLowerCase() === "both-medical(default)") {
      setBenefitTypes(["Medical", "Pharmacy"]);
      setSelectedBenefitType("Medical");
      setBenefitType("Medical");
    } else if (config.coverageFinderConfiguration.benefitType.toLowerCase() === "both-pharmacy(default)") {
      setBenefitTypes(["Pharmacy", "Medical"]);
      setSelectedBenefitType("Pharmacy");
      setBenefitType("Pharmacy");
    } else {
      setBenefitTypes([config.coverageFinderConfiguration.benefitType]);
      setSelectedBenefitType(config.coverageFinderConfiguration.benefitType);
      setBenefitType(config.coverageFinderConfiguration.benefitType);
    }
  }

  function updateAllConfigValues(config) {
    updateBenefitType(config);
    setHeader(config.coverageFinderConfiguration?.header);
    const clientShowAllTabName = config?.coverageFinderChannelAdditionalDetails?.showAllTabName ?? "Show All";
    setShowAllTabName(clientShowAllTabName);
    setGridColumnsBothBenefits(config.coverageFinderGridColumns);
    const cfSettings = config.settings;
    setSettings(cfSettings);
    if (config.indicationStylesheetURL) {
      setStylePath(config.indicationStylesheetURL);
    }

    const showAll = [];
    //FTW - SettingId 91: CF - Hide "Show All" Channel Option
    if (cfSettings?.findIndex((setting) => (setting.settingId === 91 || setting.settingId === 92) && setting.isEnabled === true) < 0) {
      showAll.push(clientShowAllTabName);
    }

    let allChannels;
    if (config.projectChannels && config.projectChannels.length > 0) {
      allChannels = showAll.concat(config.projectChannels?.map((c) => c.channelName));
    } else {
      allChannels = showAll.concat(channels);
    }

    //FTW - SettingId 92: CF-Show All Last Channel
    const hasSetting92 = cfSettings?.some(setting => setting.settingId === 92 && setting.isEnabled);
    const hasSetting91 = cfSettings?.some(setting => setting.settingId === 91 && setting.isEnabled);
    if (hasSetting92 && !hasSetting91) {
      allChannels.push(clientShowAllTabName);
    }
    setChannels(allChannels);
    setChannelFilter(allChannels[0]);

    //FTW - SettingId 82: CF-Hide Channel Tabs
    if (cfSettings?.findIndex((setting) => setting.settingId === 82 && setting.isEnabled === true) > -1) {
      setChannels([]);
      setChannelFilter(clientShowAllTabName);
    }
    setShowMap(false);
    if (cfSettings?.findIndex((setting) => setting.settingId === 79 && setting.isEnabled === true) > -1) {
      setShowMap(true);
    }

    setDebugMode(false);
    if (cfSettings?.findIndex((setting) => setting.settingId === 81 && setting.isEnabled === true) > -1) {
      setDebugMode(true);
    }

    setShowDataSource(false);
    if (cfSettings?.findIndex((setting) => setting.settingId === 85 && setting.isEnabled === true) > -1) {
      setShowDataSource(true);
    }
    const indicationName = config.coverageFinderConfiguration?.indicationName ?? config.indicationName;
    setIndicationName(indicationName);
  }

  function matchesCriteria(record, criteriaObj) {
    // Split coverageFinderColumnTitles into an array of column titles
    var columns = criteriaObj.coverageFinderColumnTitles.split(",");
    // Check if DataField of the record exists in the columns array
    return columns.includes(record.dataField);
  }

  function getFilterGridColumns(benefitTypeId, filterGridColumns) {
    let filters = appliedConfiguration.coverageFinderFilters?.find(
      (x) => x.benefitTypeId == benefitTypeId && x.enabled
    );
    const valueSelected = filters?.filterCriteria.filter((criteria) =>
      filterValue.some((selected) => selected.filterCriteria === criteria.filterCriteria)
    );
    let filtercolumn = filterGridColumns?.filter((record) =>
      // Check if there is any criteria in data2 where the record matches
      valueSelected?.some((criteriaObj) => matchesCriteria(record, criteriaObj))
    );
    let filterGridDataFieldColumns = filters?.filterCriteria.map((item) => item.coverageFinderColumnTitles);
    if (filterGridDataFieldColumns) {
      let staticColumns = filterGridColumns?.filter((record) => {
        let includeStaticColumn = true;
        for (let i = 0; i < filterGridDataFieldColumns.length; i++) {
          let columnDataFields = filterGridDataFieldColumns[i].split(",");
          let includedInFiterColumns = columnDataFields.includes(record.dataField);
          if (includedInFiterColumns) {
            includeStaticColumn = false;
            break;
          }
        }
        return includeStaticColumn;
      });
      filterGridColumns = [...staticColumns, ...filtercolumn];
    }

    return filterGridColumns;
  }
  const handleGroupProductConfiguration = (configResponse, jwtWebsiteType) => {
    const config = configResponse.configurations.find(
      (x) =>
        Number(x.indicationId) === Number(indicationid) &&
        x.coverageFinderConfiguration != null &&
        Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType &&
        Number(x.productId) === Number(tokenProducts[0].ProductId)
    );

    const settings = config?.settings;
    if (settings) {
      const allowIndication = settings.some(
        (setting) => setting.settingId === 77 && setting.isEnabled === true
      );

      let updatedProducts = [];

      if (allowIndication) {
        updatedProducts = configResponse.products
          .filter((item) => item.indication?.id === Number(config?.indicationId))
          .map((item) => ({
            ProductId: item.productId,
            Name: item.name,
          }));
      } else {
        updatedProducts = configResponse.products.map((item) => ({
          ProductId: item.productId,
          Name: item.name,
        }));
      }

      tokenProducts = updatedProducts;
      setProducts(updatedProducts);
    }

    setgroupProducts(configResponse.products);
    return config;
  };

  const applyConfiguration = (configResponse) => {
    if (configResponse) {
      setClientProductConfigurations(configResponse);

      let jwtWebsiteType = Number(CommonService.parseJwt(token).websiteType ?? 1);
      let config = jwtWebsiteType
        ? configResponse.configurations.find(
          (x) =>
            x.coverageFinderConfiguration != null &&
            Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType
        )
        : configResponse.configurations[0];

      if (indicationid) {
        config = configResponse.configurations.find(
          (x) =>
            Number(x.indicationId) === Number(indicationid) &&
            x.coverageFinderConfiguration != null &&
            Number(x.coverageFinderConfiguration.websiteType) === jwtWebsiteType
        );
      }
      if (coverageFinderGroupId > 0 && configResponse.products) {
        const groupConfig = handleGroupProductConfiguration(configResponse, jwtWebsiteType);
        config = groupConfig || config;
      }
      setAppliedConfiguration(config);
      updateAllConfigValues(config);
    } else {
      console.error("Config response is null or empty");
    }
  };

  const handleShowResults = (show) => {
    setShowResults(show);
    if (!show) {
      setRows([]);
      setFilteredRows([]);
      setOrderBy("");
      setResultMessage("");
      CommonService.hideCfHeaderStatement(show, dynamicHtmlRef);
    }
  };

  const handleRequestSort = (event, property) => {
    let sortOrder = "asc";
    if (orderBy === property && order === "asc") {
      sortOrder = "desc";
    } else if (orderBy === property && order === "desc") {
      sortOrder = "";
    }
    setOrder(sortOrder);
    setOrderBy(property);
  };
  const handleFilterChange = async (value, isFilterSame) => {
    setFilterValues(value);
    setIsFilterSame(isFilterSame);
  };
  const handleChangeBenefitType = async (newBenefitType) => {
    if (newBenefitType && !disableBenefitType) {
      saveScrollPosition();      
      setBenefitType(newBenefitType);
      setSelectedBenefitType(newBenefitType);
      const benefitTypeId = newBenefitType.toLowerCase().includes("pharmacy") ? 2 : 1;
      let filters = appliedConfiguration.coverageFinderFilters?.find(
        (x) => x.benefitTypeId == benefitTypeId && x.enabled
      );
      if (filters && !isFilterSame) {
        setResultMessage("");
        setIsLoadingCoverage(true);
        setIsLoadingCoverage(false);
      } else if (searchTermDisplay != "") {
        setShowResults(true);
        setIsLoadingCoverage(true);
        const coverageResponse = await coverageSearch(newBenefitType);
        setRows(coverageResponse?.coverages?.filter((obj) => obj.Channel !== ""));
        setFilteredRows(coverageResponse?.coverages?.filter((obj) => obj.Channel !== ""));
        setDynamicStatement(coverageResponse.statement);
        filterCoverageTable(
          coverageResponse?.coverages?.filter((obj) => obj.Channel !== ""),
          newBenefitType
        );
        setIsLoadingCoverage(false);        
        setTimeout(() => {
          restoreScrollPosition();
        }, 1);
      }
      
    }
  };

  const coverageSearch = async (newBenefitType) => {
    const benefitTypeId = newBenefitType === "Pharmacy" ? 2 : 1;
    let rawGridColumns = gridColumnsBothBenefits.filter((p) => p.benefitTypeId === benefitTypeId);
    filterDynamicColumns(benefitTypeId, rawGridColumns, channelFilter === showAllTabName);
    let searchIndication = selectedIndication != null ? selectedIndication.toString() : indicationid;
    const config = appliedConfiguration;
    if (!config) {
      console.log("Config not found for benefit type and indication");
    } else if (config.indicationId.toString() != searchIndication) {
      console.log("Config not found for indication");
    }
    let requestZipCode = null;
    let stateId = null;
    let npi = null;
    if (searchNPI.length === 10 && !isNaN(searchNPI)) {
      npi = searchNPI;
      setSearchTermDisplay(searchNPI);
    } else if (searchTerm.length > 0 && !isNaN(searchTerm[0])) {
      requestZipCode = searchTerm;
      setSearchTermDisplay(searchTerm);
    } else {
      const searchState =
        config.states.find((s) => s.name.toLowerCase() === searchTerm?.toLowerCase()) ||
        config.states.find((s) => s.name.toLowerCase() === searchTermDisplay?.toLowerCase());
      stateId = searchState?.stateId;
      setStateId(stateId);
      setSearchTermDisplay(searchState?.name);
    }
    const liveTypeId = newBenefitType === "Pharmacy" ? 4 : 1;

    if (!requestZipCode && !stateId && !npi) {
      //if a zip code was passed in to the component default to that when searching if nothing else is selected.
      requestZipCode = zipCode;
    }

    const coverageRequest = {
      ProjectId: config.projectId,
      ZipCode: requestZipCode,
      StateId: stateId,
      NPI: npi,
      ProductId: selectedProduct === null ? products[0].ProductId : selectedProduct.ProductId,
      LiveTypeId: liveTypeId,
      IndicationId: config.indicationId,
      resultName:
        newBenefitType === "Pharmacy"
          ? config.coverageFinderConfiguration.pharmacyResultName
          : config.coverageFinderConfiguration.resultName,
    };

    let rawCoverageData = await CoverageService.getInstance().getCoverage(coverageRequest);
    return rawCoverageData;
  };
  const handleSearchTerm = (value) => {
    setSearchTerm(value);
    setDisableBenefitType(true);
    //setShowResults(false);
  };
  const handleIndication = (value) => {
    saveScrollPosition();
    setSelectedIndication(value);
    setDisableBenefitType(true);
  }
  const handleCoverageSearch = async () => {
    saveScrollPosition();
    setResultMessage("");
    setIsLoadingCoverage(true);
    setShowResults(false);
    setOrderBy("");
    setSortOrder(0);
    setChannelFilter("");
    const coverageResponse = await coverageSearch(selectedBenefitType);
    if (coverageResponse != null && coverageResponse.coverages != null) {
      setDisableBenefitType(false);
      const showAll = [];
      const clientShowAllTabName = appliedConfiguration?.coverageFinderChannelAdditionalDetails?.showAllTabName ?? showAllTabName;
      setShowAllTabName(clientShowAllTabName);
      //FTW - SettingId 91: CF - Hide "Show All" Channel Option
      if (settings?.findIndex((setting) => (setting.settingId === 91 || setting.settingId === 92) && setting.isEnabled === true) < 0) {
        showAll.push(clientShowAllTabName);
      }
      let allChannels;
      if (coverageResponse.projectChannels && coverageResponse.projectChannels.length > 0) {
        allChannels = showAll.concat(coverageResponse.projectChannels?.map((c) => c.channelName));
      } else {
        allChannels = showAll.concat(coverageResponse.channels);
      }
      //FTW - SettingId 92: CF-Show All Last Channel
      const hasSetting92 = settings?.some(setting => setting.settingId === 92 && setting.isEnabled);
      const hasSetting91 = settings?.some(setting => setting.settingId === 91 && setting.isEnabled);
      if (hasSetting92 && !hasSetting91) {
        allChannels.push(clientShowAllTabName);
      }
      setChannelFilter(allChannels[0]);
      setPlanNameFilter("");
      setChannels(allChannels);
      if (settings?.findIndex((setting) => setting.settingId === 82 && setting.isEnabled === true) > -1) {
        setChannels([]);
        setChannelFilter(clientShowAllTabName);
      }
      setRows(coverageResponse.coverages.filter((obj) => obj.Channel !== ""));
      setFilteredRows(coverageResponse.coverages.filter((obj) => obj.Channel !== ""));
      setDynamicStatement(coverageResponse.statement);

      if (coverageResponse.zipCode) {
        setSearchTermDisplay(coverageResponse.zipCode);
      } else if (coverageResponse.stateId) {
        setStateId(coverageResponse.stateId);
        var stateName = appliedConfiguration.states.find((s) => s.stateId === coverageResponse.stateId)?.name;
        setSearchTermDisplay(stateName);
      }

      if (coverageTableContainerRef?.current) {
        coverageTableContainerRef.current.scrollTo(0, 0);
      }

      setShowResults(true);
      CommonService.hideCfHeaderStatement(true, dynamicHtmlRef);
      setShowBenefitType(benefitTypes?.length > 1 ?? false);
      setIsLoadingCoverage(false);
      
      setTimeout(() => {
        restoreScrollPosition();
      }, 1);
    } else {
      setIsLoadingCoverage(false);
      setShowBenefitType(false);
      if (searchTerm || searchTermDisplay || searchNPI) {
        setResultMessage(NO_RESULTS_FOUND_MESSAGE);
      }
      
      setTimeout(() => {
        restoreScrollPosition();
      }, 1);
    }
  };
  const onChangeProduct = async (product) => {
    saveScrollPosition();
    setSelectedProduct(product);
    setProductId(product.ProductId);
    setProductName(product.Name);
    updateConfigurationOnProductChange(product.ProductId)
  };

  function sortRows(newSortState) {
    let sortedData = [...filteredRows];
    if (newSortState === 0) {
      setvisibleRows(sortedData);
      return;
    }
    const key = dynamicColumns[0]?.dataField;

    if (newSortState === 1) {
      sortedData.sort((a, b) => a[key].toString().localeCompare(b[key].toString()));
    } else if (newSortState === 2) {
      sortedData.sort((a, b) => b[key].toString().localeCompare(a[key].toString()));
    }
    setvisibleRows(sortedData);
  }


  return (
    <>
      <link type="text/css" rel="stylesheet" href={stylePath} />
      {isLoadingPage && (
        <div className="norstella-cf-overlay">
          <LoaderWithIcon></LoaderWithIcon>
        </div>
      )}
      {!isLoadingPage && (
        <Box
          id="coverageFinderBox"
          className="norstella-cf-coverageFinderBox"
          sx={{ flexGrow: 1 }}
          part="cfCoverageFinderBox"
        >
          <Grid container spacing={2} direction={"column"} alignItems={"left"} className="norstella-cf-Grid">
            <Grid ref={dynamicHtmlRef} item className="norstella-cf-GridItemHeader">
              {/* Header */}
              <DynamicHtml
                input={header != null ? header : ""}
                className="norstella-cf-header"
                part="cfDivHeaderText"
              />
            </Grid>

            {!matchesMobileWidth && showMap && (
              <Grid item part="cfStateMap" className="norstella-cf-GridItemStateMap">
                <StateMap
                  states={appliedConfiguration.states}
                  selectedState={selectedState}
                  setSelectedState={setSelectedState}
                  setShowResults={handleShowResults}
                />
              </Grid>
            )}

            <Grid item className="norstella-cf-GridItemSearch">
              <div className="norstella-cf-div-row">
                <div className="norstella-cf-divSearchCoverage">
                  <SearchCoverage
                    clientProductConfigurations={clientProductConfigurations}
                    setShowResults={handleShowResults}
                    onChangeBenefitType={handleChangeBenefitType}
                    handleFilterChange={handleFilterChange}
                    handleProductChange={onChangeProduct}
                    onChangeNPI={setNPI}
                    onChangeSearchTerm={handleSearchTerm}
                    onChangeIndication={handleIndication}
                    handleCoverageSearch={handleCoverageSearch}
                    selectedState={selectedState}
                    setSelectedState={setSelectedState}
                    benefitTypes={benefitTypes}
                    setResultMessage={setResultMessage}
                    selectedBenefitType={selectedBenefitType}
                    selectedProduct={selectedProduct}
                    products={products}
                    zipCode={zipCode}
                    setShowBenefitType={setShowBenefitType}
                  />
                </div>
                {!matchesMobileWidth && benefitTypes?.length > 1 && showBenefitType && (
                  <div className="norstella-cf-divSearchCoverage">
                    <BenefitTypeButton
                      selectedBenefitType={selectedBenefitType}
                      benefitTypes={benefitTypes}
                      onChangeBenefitType={handleChangeBenefitType}
                      searchNPI={searchNPI}
                      disableBenefitType={disableBenefitType}
                    />
                  </div>
                )}
              </div>
              <div aria-live="polite" aria-atomic="true">
                {resultMessage && resultMessage.trim() !== "" && (
                  <div className="norstella-cf-resultmessage" part="cfResultmessage">
                    {resultMessage}
                  </div>
                )}
              </div>
            </Grid>

            <div aria-live="polite" aria-atomic="true" >
              {isLoadingCoverage && "Loading"}
            </div>
            {isLoadingCoverage && (
              <div className="norstella-cf-overlay">
                <LoaderWithIcon></LoaderWithIcon>
              </div>
            )}
            {matchesMobileWidth && benefitTypes?.length > 1 && showBenefitType && (
              <Grid item className="norstella-cf-GridItemBenefitTypeButton">
                <BenefitTypeButton
                  selectedBenefitType={selectedBenefitType}
                  benefitTypes={benefitTypes}
                  onChangeBenefitType={handleChangeBenefitType}
                  searchNPI={searchNPI}
                  disableBenefitType={disableBenefitType}
                />
              </Grid>
            )}
            {showResults && (
              <>
                {subHeader && subHeader.trim() !== "" && (
                  <Grid item className="norstella-cf-GridItemSubHeader">
                    {/* SubHeader */}
                    <DynamicHtml
                      input={subHeader != null ? subHeader : ""}
                      className="norstella-cf-headerSubHeader"
                      part="cfDivSubHeaderText"
                    />
                  </Grid>
                )}
                <Grid item className="norstella-cf-GridItemStatement">
                  {/* Dynamic Statement */}
                  <Statement dynamicStatement={dynamicStatement} />
                </Grid>
                <div id="divCoverageFilterAndTable" className="norstella-cf-coverageFilterAndTable">
                  <Grid item className="norstella-cf-GridItemFilterCoverage">
                    <FilterCoverage
                      channels={channels}
                      planSearch={setPlanNameFilter}
                      channelFilter={setChannelFilter}
                      setSortOrder={setSortOrder}
                    />
                  </Grid>
                  <Grid item id="displayCoverageLayoutGridItem" className="norstella-cf-displayCoverageLayoutGridItem">
                    {!isLoadingCoverage && (
                      <DisplayCoverage
                        dynamicColumns={dynamicColumns}
                        tableRows={visibleRows}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        ref={coverageTableContainerRef}
                        stateId={stateId}
                        noDataWarning={noDataWarning}
                        showDataSource={showDataSource}
                        channelFilter={channelFilter}
                      />
                    )}
                  </Grid>
                </div>
                <Grid item className="norstella-cf-GridItemFooter">
                  <Footer className="norstella-cf-customFooter" customFooter={customFooter} />
                </Grid>
              </>
            )}
            {debugMode && (
              <Grid item className="norstella-cf-GridItemDebugMessage">
                <DebugMessage />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </>
  );
};

export default HCPCoverageFinder;
